import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Banner from "../components/Banner";
import backgroundImage from "../img/banner/ZTO-Banner.png";
import "../css/policyDetail.css";

const PolicyDetail = () => {
  const { slug } = useParams();
  const [activeSection, setActiveSection] = useState("");

  const sections = getPolicySections(slug);

  const handleScrollTo = (id) => {
    const target = document.getElementById(id);
    if (!target) return;
    const headerOffset = 100;
    const top =
      target.getBoundingClientRect().top + window.pageYOffset - headerOffset;
    window.scrollTo({ top, behavior: "smooth" });
  };

  // Scroll spy - track active section
  useEffect(() => {
    const handleScroll = () => {
      const allSections = [];
      sections.forEach((section) => {
        allSections.push(section);
        if (section.children) {
          allSections.push(...section.children);
        }
      });

      const sectionsData = allSections
        .map((section) => {
          const element = document.getElementById(section.id);
          if (element) {
            const rect = element.getBoundingClientRect();
            return {
              id: section.id,
              top: rect.top,
              bottom: rect.bottom,
            };
          }
          return null;
        })
        .filter(Boolean);

      // Find the section that's currently in view
      const current = sectionsData.find(
        (section) => section.top <= 150 && section.bottom > 150
      );

      if (current) {
        setActiveSection(current.id);
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Initial check

    return () => window.removeEventListener("scroll", handleScroll);
  }, [sections]);

  const renderPolicyContent = () => {
    switch (slug) {
      case "general-policy":
        return <GeneralPolicy />;
      case "secure-personal-info":
        return <SecurePersonalInfoPolicy />;
      case "check-order":
        return <CheckOrderPolicy />;
      case "shipping-from-china":
        return <RegulationsOnShippingFromChina />;
      case "complaint-resolution":
        return <ServiceComplaintResolutionPolicy />;
      default:
        return (
          <div className="card p-3 w-100 shadow-sm border">
            <h3 className="text-center">Chính sách không tồn tại</h3>
          </div>
        );
    }
  };

  const getPolicyTitle = () => {
    const titles = {
      "general-policy": "Chính Sách Chung",
      "secure-personal-info": "Chính Sách Bảo Mật Thông Tin Cá Nhân",
      "check-order": "Chính Sách Kiểm Hàng",
      "shipping-from-china": "Quy Định Về Ký Gửi Hàng Trung Quốc",
      "complaint-resolution": "Chính Sách Giải Quyết Khiếu Nại Dịch Vụ",
    };
    return titles[slug] || "Chính sách";
  };

  return (
    <div className="policy-detail-page">
      <Header />
      <Banner
        title={getPolicyTitle()}
        backgroundImage={backgroundImage}
        breadcrumb={
          <>
            <Link to={"/"} className="text-white p-2">
              Trang chủ
            </Link>
            <span>{">"}</span>
            <Link to={"/chinh-sach"} className="text-white p-2">
              Chính sách
            </Link>
            <span>{">"}</span>
            <span className="text-white p-2">{getPolicyTitle()}</span>
          </>
        }
      />
      <div className="container-fluid p-3">
        <div className="row g-4">
          {sections.length > 0 && (
            <div className="col-lg-3 d-none d-lg-block">
              <div className="policy-toc sticky-sidebar">
                <h6 className="policy-toc-title">Mục lục</h6>
                <ul className="policy-toc-list">
                  {sections.map((item, index) => (
                    <li key={item.id}>
                      <button
                        type="button"
                        className={`policy-toc-link ${
                          activeSection === item.id ? "active" : ""
                        }`}
                        onClick={() => handleScrollTo(item.id)}
                      >
                        <span className="toc-number">{index + 1}.</span>
                        <span className="toc-text">{item.label}</span>
                      </button>
                      {item.children && item.children.length > 0 && (
                        <ul className="policy-toc-sublist">
                          {item.children.map((child, childIndex) => (
                            <li key={child.id}>
                              <button
                                type="button"
                                className={`policy-toc-link policy-toc-sublink ${
                                  activeSection === child.id ? "active" : ""
                                }`}
                                onClick={() => handleScrollTo(child.id)}
                              >
                                <span className="toc-number">
                                  {index + 1}.{childIndex + 1}.
                                </span>
                                <span className="toc-text">{child.label}</span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
          <div className={sections.length > 0 ? "col-lg-8 col-12" : "col-12"}>
            {renderPolicyContent()}
          </div>
        </div>
        <div className="text-center mt-4">
          <Link to="/chinh-sach" className="btn btn-primary">
            <i className="fas fa-arrow-left me-2"></i>
            Quay lại danh sách chính sách
          </Link>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PolicyDetail;

const getPolicySections = (slug) => {
  const map = {
    "general-policy": [
      {
        id: "general-bao-hanh",
        label: "Chính Sách Bảo Hành",
        children: [
          { id: "general-bao-hanh-1", label: "Mất Hàng - Không bảo hiểm" },
          { id: "general-bao-hanh-2", label: "Mất Hàng - Có bảo hiểm" },
          { id: "general-bao-hanh-3", label: "Bảo Hành Nhà Sản Xuất" },
        ],
      },
      {
        id: "general-tranh-chap",
        label: "Chính Sách Giải Quyết Tranh Chấp",
        children: [
          { id: "general-tranh-chap-1", label: "Tiếp Nhận Khiếu Nại" },
          { id: "general-tranh-chap-2", label: "Trách Nhiệm Giải Quyết" },
        ],
      },
      {
        id: "general-doi-tra",
        label: "Chính Sách Đổi Trả Hàng Hóa",
        children: [
          { id: "general-doi-tra-1", label: "Điều Kiện Đổi/Trả" },
          { id: "general-doi-tra-2", label: "Thời Hạn Đổi/Trả" },
          { id: "general-doi-tra-3", label: "Hình Thức Hoàn Phí" },
        ],
      },
    ],
    "secure-personal-info": [
      { id: "secure-1", label: "Mục đích và phạm vi" },
      { id: "secure-2", label: "Phạm vi sử dụng thông tin" },
      { id: "secure-3", label: "Thời gian lưu trữ" },
      { id: "secure-4", label: "Quyền truy cập/chỉnh sửa" },
      { id: "secure-5", label: "Cam kết bảo mật" },
      { id: "secure-6", label: "Cơ chế khiếu nại" },
      { id: "secure-7", label: "Thông tin liên hệ hỗ trợ khách hàng" },
    ],
    "check-order": [
      { id: "check-quyen", label: "Quyền kiểm tra hàng hóa" },
      { id: "check-pham-vi", label: "Phạm vi kiểm tra" },
      { id: "check-tu-choi", label: "Từ chối nhận hàng" },
      { id: "check-quy-trinh", label: "Quy trình kiểm tra" },
      { id: "check-trach-nhiem", label: "Trách nhiệm sau giao hàng" },
      { id: "check-luu-y", label: "Lưu ý khi mở gói" },
    ],
    "shipping-from-china": [
      { id: "ship-dieu-kien", label: "Điều kiện và quy trình ký gửi" },
      { id: "ship-quy-trinh", label: "Quy trình ký gửi" },
      { id: "ship-trach-nhiem", label: "Trách nhiệm khi không tuân thủ" },
      { id: "ship-cam-hang", label: "Các mặt hàng không nhận" },
      { id: "ship-boi-thuong", label: "Chính sách khiếu nại/bồi thường" },
      { id: "ship-lien-he", label: "Liên hệ" },
    ],
    "complaint-resolution": [
      { id: "complaint-tiep-nhan", label: "Tiếp nhận và xử lý" },
      { id: "complaint-boi-thuong", label: "Chính sách bồi thường" },
      { id: "complaint-luu-kho", label: "Quy định lưu kho" },
      { id: "complaint-ho-tro", label: "Hỗ trợ ngoài phạm vi" },
      { id: "complaint-luu-y", label: "Các lưu ý" },
    ],
  };

  return map[slug] || [];
};

const GeneralPolicy = () => {
  return (
    <div className="card p-3 w-100 shadow-sm border">
      <h3 className="text-center policy-title">CHÍNH SÁCH CHUNG</h3>
      <p id="general-bao-hanh" className="fw-bold policy-sub-title">
        Chính Sách Bảo Hành
      </p>
      <p id="general-bao-hanh-1" className="fw-bold">
        1. Mất Hàng - Không sử dụng bảo hiểm hàng hóa:
      </p>
      <p>
        Nếu hàng hóa bị mất trong quá trình vận chuyển và bạn không sử dụng dịch
        vụ bảo hiểm hàng hóa của <strong>ZTO VIỆT NAM</strong>, chúng tôi chỉ
        bồi thường gấp 3 lần số tiền vận chuyển của đơn hàng.
      </p>
      <p id="general-bao-hanh-2" className="fw-bold">
        2. Mất Hàng - Sử dụng bảo hiểm hàng hóa:
      </p>
      <p>
        Nếu bạn sử dụng dịch vụ bảo hiểm hàng hóa (phí bảo hiểm là 5% giá trị
        hàng hóa kê khai), <strong>ZTO VIỆT NAM</strong> sẽ bồi thường 50% giá
        trị kiện hàng mà bạn đã kê khai.
      </p>
      <p id="general-bao-hanh-3" className="fw-bold">
        3. Bảo Hành Theo Chính Sách Nhà Sản Xuất:
      </p>
      <p>
        Chính sách bảo hành đối với từng sản phẩm, hàng hóa, dịch vụ sẽ do Nhà
        sản xuất/Đơn vị phân phối quy định. <strong>ZTO VIỆT NAM</strong> sẽ hỗ
        trợ khách hàng liên hệ với nhà sản xuất hoặc đơn vị phân phối để giải
        quyết bảo hành.
      </p>
      <p id="general-tranh-chap" className="fw-bold policy-sub-title">
        Chính Sách Giải Quyết Tranh Chấp
      </p>
      <p id="general-tranh-chap-1" className="fw-bold">
        1. Tiếp Nhận Khiếu Nại:
      </p>
      <p>
        <strong>ZTO VIỆT NAM</strong> sẽ tiếp nhận mọi khiếu nại liên quan đến
        giao dịch tại website <strong>ZTO VIỆT NAM</strong> và hỗ trợ khách hàng
        một cách tối đa.
      </p>
      <p>
        Các khiếu nại có thể liên quan đến thông tin đăng tải, chính sách bảo
        mật, chất lượng sản phẩm, v.v.
      </p>
      <p>Cách thức liên hệ khiếu nại:</p>
      <p>Gọi điện thoại: 036.475.8606</p>
      <p>Nhắn tin Zalo: 036.475.8606</p>
      <p id="general-tranh-chap-2" className="fw-bold">
        2. Trách Nhiệm Giải Quyết Khiếu Nại:
      </p>
      <p>
        Khiếu nại liên quan đến dịch vụ của <strong>ZTO VIỆT NAM</strong> sẽ
        được giải quyết trong vòng 48 giờ kể từ khi nhận được khiếu nại.
      </p>
      <p>
        Nếu các bên không đạt được thỏa thuận qua thương lượng hoặc hòa giải,
        một trong hai bên có thể đưa vụ việc ra cơ quan nhà nước có thẩm quyền
        để giải quyết.
      </p>
      <p id="general-doi-tra" className="fw-bold policy-sub-title">
        Chính Sách Đổi Trả Hàng Hóa
      </p>
      <p id="general-doi-tra-1" className="fw-bold">
        1. Điều Kiện Đổi/Trả Sản Phẩm:
      </p>
      <p>Lỗi kỹ thuật do nhà sản xuất.</p>
      <p>Sản phẩm bị rách, nứt, hư hỏng hoặc có dấu hiệu đã qua sử dụng.</p>
      <p>
        Sản phẩm không đúng so với đơn hàng về số lượng, mã sản phẩm, kích
        thước, màu sắc, chủng loại.
      </p>
      <p id="general-doi-tra-2" className="fw-bold">
        2. Thời Hạn Đổi/Trả Sản Phẩm:
      </p>
      <p>Quý khách có thể đổi/trả sản phẩm trong quá trình kiểm tra hàng.</p>
      <p>
        Sau khi ký nhận hàng và thanh toán, việc yêu cầu đổi trả sẽ không được
        chấp nhận.
      </p>
      <p id="general-doi-tra-3" className="fw-bold">
        3. Hình Thức Hoàn Phí:
      </p>
      <p>
        Nếu Quý khách đã thanh toán trước khi nhận hàng và đủ điều kiện đổi/trả
        sản phẩm, sau khi sản phẩm được hoàn trả, tiền sẽ được hoàn lại trong
        vòng 5-15 ngày làm việc qua hình thức chuyển khoản ngân hàng.
      </p>
    </div>
  );
};

const SecurePersonalInfoPolicy = () => {
  return (
    <div className="card p-3 w-100 shadow-sm border">
      <h3 className="text-center policy-title">
        CHÍNH SÁCH BẢO MẬT THÔNG TIN CÁ NHÂN
      </h3>
      <p>
        <strong>ZTO VIỆT NAM</strong> cam kết bảo vệ quyền lợi và thông tin cá
        nhân của khách hàng khi sử dụng dịch vụ của chúng tôi. Dưới đây là thông
        tin chi tiết về cách thức chúng tôi thu thập, sử dụng và bảo vệ thông
        tin cá nhân của khách hàng.
      </p>
      <p id="secure-1" className="fw-bold policy-sub-title">
        1. Mục đích và phạm vi thu thập thông tin
      </p>
      <p>
        Thông tin cá nhân của khách hàng được chúng tôi thu thập chủ yếu qua
        website, bao gồm họ tên, email, số điện thoại và địa chỉ giao hàng. Đây
        là những thông tin cần thiết để khách hàng có thể đăng ký tài khoản và
        sử dụng dịch vụ tại <strong>ZTO VIỆT NAM</strong>. Mục đích thu thập
        thông tin là để:
      </p>
      <p>
        • Quản lý tài khoản khách hàng.<br></br>• Xác nhận thông tin đơn hàng và
        giao dịch.<br></br>• Liên lạc với khách hàng để đảm bảo quyền lợi và
        hoàn tất giao dịch.
      </p>
      <p>
        Khách hàng cần chịu trách nhiệm bảo mật thông tin tài khoản của mình và
        thông báo ngay cho chúng tôi nếu có sự cố về bảo mật hoặc thông tin cá
        nhân bị lạm dụng.
      </p>
      <p id="secure-2" className="fw-bold policy-sub-title">
        2. Phạm vi sử dụng thông tin
      </p>
      <p>
        Thông tin cá nhân của khách hàng chỉ được sử dụng cho các mục đích sau:
      </p>
      <p>
        • Xác nhận đơn hàng và thực hiện giao dịch giao hàng.<br></br>• Cung cấp
        thông tin về sản phẩm và dịch vụ theo yêu cầu của khách hàng.<br></br>•
        Gửi email thông báo các chương trình khuyến mãi, ưu đãi và các thông tin
        liên quan đến sản phẩm của <strong>ZTO VIỆT NAM</strong>.<br></br>• Liên
        hệ với khách hàng trong những trường hợp cần thiết, chẳng hạn như giải
        quyết sự cố hoặc yêu cầu hỗ trợ.
      </p>
      <p>
        Chúng tôi cam kết không sử dụng thông tin cá nhân của khách hàng ngoài
        các mục đích trên.
      </p>
      <p id="secure-3" className="fw-bold policy-sub-title">
        3. Thời gian lưu trữ thông tin
      </p>
      <p>
        Thông tin cá nhân của khách hàng sẽ được lưu trữ trên máy chủ của chúng
        tôi cho đến khi khách hàng yêu cầu hủy bỏ hoặc khi có yêu cầu từ quản
        trị viên. Dù vậy, chúng tôi cam kết bảo mật mọi thông tin cá nhân trong
        suốt thời gian lưu trữ.
      </p>
      <p id="secure-4" className="fw-bold policy-sub-title">
        4. Quyền truy cập và chỉnh sửa thông tin cá nhân
      </p>
      <p>
        Khách hàng có quyền kiểm tra, chỉnh sửa hoặc yêu cầu xóa bỏ thông tin cá
        nhân của mình. Để thực hiện các quyền này, khách hàng vui lòng liên hệ
        với chúng tôi qua các phương thức liên hệ dưới đây.
      </p>
      <p id="secure-5" className="fw-bold policy-sub-title">
        5. Cam kết bảo mật thông tin cá nhân
      </p>
      <p>
        <strong>ZTO VIỆT NAM</strong> cam kết bảo vệ thông tin cá nhân của khách
        hàng một cách nghiêm ngặt. Chúng tôi chỉ thu thập và sử dụng thông tin
        khi có sự đồng ý của khách hàng, trừ trường hợp pháp luật yêu cầu khác.
      </p>
      <p>
        • Chúng tôi sẽ không cung cấp, chia sẻ hoặc tiết lộ thông tin cá nhân
        của khách hàng cho bất kỳ bên thứ ba nào mà không có sự đồng ý của khách
        hàng.<br></br>• Trong trường hợp dữ liệu cá nhân của khách hàng bị mất
        mát hoặc rò rỉ do sự tấn công của hacker, chúng tôi sẽ thông báo ngay
        cho cơ quan chức năng và khách hàng về sự cố này, đồng thời thực hiện
        các biện pháp khắc phục.
      </p>
      <p>
        Mọi thông tin giao dịch trực tuyến của khách hàng, bao gồm hóa đơn và
        chứng từ số hóa, sẽ được bảo mật tuyệt đối.
      </p>
      <p id="secure-6" className="fw-bold policy-sub-title">
        6. Cơ chế tiếp nhận và giải quyết khiếu nại về việc sử dụng sai mục đích
        thông tin cá nhân
      </p>
      <p>
        <strong>ZTO VIỆT NAM</strong> cam kết bảo vệ quyền lợi của khách hàng
        đối với việc sử dụng thông tin cá nhân. Nếu khách hàng phát hiện thông
        tin cá nhân của mình bị sử dụng sai mục đích hoặc vượt quá phạm vi đã
        thông báo, khách hàng có thể gửi khiếu nại và các chứng cứ liên quan qua
        số điện thoại <strong>036.475.8606</strong>
      </p>
      <p>
        Chúng tôi sẽ phản hồi trong vòng <strong>5 ngày làm việc</strong> (trừ
        thứ Bảy và Chủ Nhật) kể từ khi nhận được khiếu nại.
      </p>
      <p>
        Mọi tranh chấp hoặc khiếu nại phát sinh sẽ được giải quyết thông qua
        thương lượng, hòa giải, hoặc trọng tài. Nếu cần thiết, chúng tôi sẽ đưa
        vụ việc ra Tòa án theo quy định của pháp luật.
      </p>
      <p id="secure-7" className="policy-sub-title fw-bold">
        7. Thông tin liên hệ hỗ trợ khách hàng:
      </p>
      <p>
        • 📞 Hotline: 036.475.8606<br></br>• 🏢 Trụ sở: Số nhà 55, đường 181, xã
        Phú Thị, huyện Gia Lâm , TP Hà Nội, Gia Lâm, Vietnam
      </p>

      <p>
        Chúng tôi cam kết luôn bảo mật thông tin của khách hàng và đảm bảo quyền
        lợi của bạn trong mọi trường hợp. Cảm ơn quý khách đã tin tưởng và sử
        dụng dịch vụ của <strong>ZTO VIỆT NAM</strong>!
      </p>
    </div>
  );
};

const CheckOrderPolicy = () => {
  return (
    <div className="card p-3 w-100 shadow-sm border">
      <h3 className="text-center policy-title">CHÍNH SÁCH KIỂM HÀNG</h3>
      <p id="check-quyen" className="fw-bold policy-sub-title">
        1. Quyền kiểm tra hàng hóa:
      </p>
      <p>
        Trước khi thanh toán và nhận hàng, khách hàng có quyền kiểm tra sản phẩm
        ngay tại chỗ. Tuy nhiên, ZTO VIỆT NAM{" "}
        <strong>không hỗ trợ thử hàng</strong>.<br></br>
        Khách hàng cần mở gói hàng để kiểm tra, đảm bảo sản phẩm nhận được đúng
        mẫu mã, số lượng, chủng loại theo đơn đặt hàng.
        <br></br>
        Khách hàng không được phép thử hay sử dụng sản phẩm. Sau khi đồng ý với
        món hàng, khách hàng sẽ thanh toán cho nhân viên giao hàng (nếu là hình
        thức COD) và ký nhận hàng.
      </p>
      <p id="check-pham-vi" className="fw-bold policy-sub-title">
        2. Phạm vi kiểm tra hàng hóa:
      </p>
      <p>
        Khách hàng được phép kiểm tra ngoại quan sản phẩm như số lượng, mã sản
        phẩm, kích thước, màu sắc.<br></br>
        Không kiểm tra: Việc mở seal (niêm phong) của sản phẩm, xé bao bì/tem
        nhãn hay bất kỳ hành động nào gây ảnh hưởng đến chất lượng sản phẩm.
      </p>
      <p id="check-tu-choi" className="fw-bold policy-sub-title">
        3. Từ chối nhận hàng:
      </p>
      <p>
        Nếu không hài lòng với sản phẩm, khách hàng có thể từ chối nhận hàng.
        <br></br>
        Trong trường hợp này, phí hoàn hàng sẽ được tính tương đương với phí
        ship của đơn hàng.
      </p>
      <p id="check-quy-trinh" className="fw-bold policy-sub-title">
        4. Quy trình kiểm tra:
      </p>
      <p>
        Khi khách hàng kiểm tra đơn hàng, nhân viên giao nhận phải chờ đợi khách
        hàng kiểm tra hàng hóa trong gói hàng.<br></br>
        Nếu nhân viên từ chối cho kiểm tra, khách hàng có thể liên hệ với ZTO
        VIỆT NAM qua số điện thoại hỗ trợ: 036.475.8606
      </p>
      <p id="check-trach-nhiem" className="fw-bold policy-sub-title">
        5. Trách nhiệm sau giao hàng:
      </p>
      <p>
        ZTO VIỆT NAM <strong>không chịu trách nhiệm</strong> về lỗi, số lượng,
        hay mẫu mã sản phẩm sau khi đơn hàng đã được giao thành công.
      </p>
      <p id="check-luu-y" className="fw-bold policy-sub-title">
        6. Lưu ý khi mở gói hàng:
      </p>
      <p>
        Khách hàng nên tránh sử dụng vật sắc nhọn để mở gói hàng nhằm tránh làm
        hư hỏng sản phẩm bên trong.<br></br>
        Nếu sản phẩm bị hư hỏng do lỗi từ phía khách hàng, ZTO VIỆT NAM sẽ không
        hỗ trợ đổi/trả hoặc bảo hành sản phẩm.
      </p>
      <p>
        Chính sách này nhằm đảm bảo quyền lợi của khách hàng trong việc nhận
        hàng và kiểm tra chất lượng sản phẩm một cách cẩn thận trước khi thanh
        toán.
      </p>
    </div>
  );
};

const RegulationsOnShippingFromChina = () => {
  return (
    <div className="card p-3 w-100 shadow-sm border">
      <h3 className="text-center policy-title">
        QUY ĐỊNH VỀ KÝ GỬI HÀNG TRUNG QUỐC
      </h3>
      <p className="fw-bold text-center">ĐIỀU KIỆN VÀ QUY TRÌNH KÝ GỬI</p>
      <p id="ship-dieu-kien" className="fw-bold policy-sub-title">
        1. Điều kiện ký gửi
      </p>
      <p>
        ZTO VIỆT NAM chỉ nhận hàng ký gửi khi khách hàng đã{" "}
        <strong>tạo đơn "HÀNG KÝ GỬI"</strong> trên hệ thống của ZTO VIỆT NAM.
      </p>
      <p id="ship-quy-trinh" className="fw-bold policy-sub-title">
        2. Quy trình ký gửi
      </p>
      <p>
        Khách hàng cần tuân thủ đúng quy trình ký gửi được yêu cầu trên hệ thống
        của ZTO VIỆT NAM, bao gồm việc tạo đơn ký gửi và cung cấp đầy đủ thông
        tin chính xác.
      </p>
      <p id="ship-trach-nhiem" className="fw-bold policy-sub-title">
        3. Trách nhiệm khi không tuân thủ quy trình
      </p>
      <p>
        Nếu khách hàng không thực hiện đúng quy trình ký gửi, như không tạo đơn
        ký gửi, cung cấp thông tin sai lệch hoặc không đầy đủ, ZTO VIỆT NAM sẽ{" "}
        <strong>không chịu trách nhiệm</strong> khi xảy ra rủi ro như mất hàng
        hoặc các sự cố khác.
      </p>
      <p id="ship-cam-hang" className="fw-bold policy-sub-title">
        4. Các mặt hàng không được ZTO VIỆT NAM nhận đặt hàng và vận chuyển
      </p>
      <p>
        Do yêu cầu về luật pháp và bảo quản hàng hóa, ZTO VIỆT NAM không nhận
        đặt hàng và vận chuyển các mặt hàng sau:
      </p>
      <p>
        1.{" "}
        <strong>
          Vũ khí, đạn dược, vật liệu nổ, trang thiết bị kỹ thuật quân sự
        </strong>{" "}
        (súng, đạn, đao, kiếm, dao kéo, ống ngắm, v.v.)<br></br>
        2. <strong>Pháo các loại</strong>, đèn trời, các loại thiết bị gây nhiễu
        máy đo tốc độ phương tiện giao thông.<br></br>
        3. <strong>Các chất ma túy và chất kích thích thần kinh</strong>.
        <br></br>
        4. <strong>Các sản phẩm dạng bột</strong>.<br></br>
        5. <strong>Các mặt hàng thực phẩm, dược phẩm, mỹ phẩm</strong>.<br></br>
        6. <strong>Hàng giả, hàng nhái</strong> (Kể cả hàng chính hãng của Nike,
        Adidas).<br></br>
        7. <strong>Sinh vật sống</strong>.<br></br>
        8. <strong>Các loại xuất bản phẩm cấm</strong> phổ biến và lưu hành tại
        Việt Nam:<br></br>
        Tem bưu chính thuộc diện cấm kinh doanh.<br></br>
        Thiết bị vô tuyến điện, thiết bị ứng dụng sóng vô tuyến không phù hợp
        với quy hoạch tần số.<br></br>
        Các loại văn hóa phẩm thuộc diện cấm.<br></br>
        9. <strong>Phương tiện vận tải tay lái bên phải</strong> (kể cả xe đã
        chuyển đổi tay lái), trừ các phương tiện chuyên dụng không tham gia giao
        thông.<br></br>
        10. <strong>Ô tô, mô tô, xe máy</strong> bị tẩy xóa, đục sửa số khung,
        số động cơ.<br></br>
        11. <strong>Vật tư, phương tiện đã qua sử dụng</strong> (ô tô, máy kéo,
        xe gắn máy, phụ tùng, động cơ, v.v.).<br></br>
        12. <strong>Hóa chất độc, thuốc bảo vệ thực vật</strong> và hóa chất
        trong Phụ lục III Công ước Rotterdam.<br></br>
        13. <strong>Các loại văn hóa phẩm đồi trụy, phản động</strong>, tài liệu
        phá hoại trật tự công cộng.<br></br>
        14. <strong>Hàng tiêu dùng đã qua sử dụng</strong> (dệt may, giày dép,
        điện tử, điện lạnh, thiết bị y tế, v.v.).<br></br>
        15. <strong>Phế liệu, phế thải, thiết bị làm lạnh sử dụng C.F.C</strong>
        .<br></br>
        16. <strong>Sản phẩm công nghệ thông tin đã qua sử dụng</strong> (điện
        thoại, laptop, v.v.).<br></br>
        17. <strong>Chất lỏng và các sản phẩm dạng lỏng</strong>.<br></br>
        18. <strong>Một số sản phẩm bị hạn chế nhập khẩu</strong> (vải cuộn,
        linh kiện máy móc công nghiệp, mỹ phẩm, màn hình máy tính, v.v.).
      </p>
      <p id="ship-boi-thuong" className="fw-bold policy-sub-title">
        5. CHÍNH SÁCH GIẢI QUYẾT KHIẾU NẠI, BỒI THƯỜNG VỚI HÀNG KÝ GỬI
      </p>
      <p>
        • ZTO VIỆT NAM không trực tiếp giao dịch với nhà cung cấp của Quý khách
        và không cung cấp dịch vụ kiểm hàng cho các đơn hàng ký gửi.<br></br>•
        Hàng hóa của Quý khách được vận chuyển đến kho của ZTO VIỆT NAM theo số
        kiện đã kê khai và chúng tôi không chịu trách nhiệm về tình trạng hư
        hỏng hoặc tính chính xác của hàng hóa trước khi đến kho.<br></br>•{" "}
        <strong>Dịch vụ bảo hiểm hàng hóa</strong>: Nếu có sự cố mất hàng, ZTO
        VIỆT NAM sẽ bồi thường 50% giá trị hàng hóa mà Quý khách kê khai. Phí
        bảo hiểm là 5% tổng giá trị đơn hàng.
        <br></br>• Nếu không sử dụng dịch vụ bảo hiểm, ZTO VIỆT NAM sẽ bồi
        thường gấp 3 lần phí vận chuyển của đơn hàng nếu có sự cố mất hoặc thất
        lạc hàng.<br></br>• <strong>Lưu ý</strong>: ZTO VIỆT NAM có quyền xác
        minh giá trị đơn hàng khi xảy ra tranh chấp. Nếu giá trị kê khai không
        đúng với giá trị thực tế, ZTO VIỆT NAM sẽ không chấp nhận đền bù thiệt
        hại.
      </p>
      <p id="ship-lien-he" className="fw-bold policy-sub-title">
        6. LIÊN HỆ
      </p>
      <p>
        • Nếu có vấn đề về đơn hàng, vui lòng liên hệ{" "}
        <strong>Hotline: 036.475.8606</strong> để được xử lý sớm nhất.
      </p>
    </div>
  );
};

const ServiceComplaintResolutionPolicy = () => {
  return (
    <div className="card p-3 w-100 shadow-sm border">
      <h3 className="text-center policy-title">
        CHÍNH SÁCH GIẢI QUYẾT KHIẾU NẠI DỊCH VỤ
      </h3>
      <p id="complaint-tiep-nhan" className="fw-bold policy-sub-title">
        1. Thời gian thực hiện, tiếp nhận và xử lý khiếu nại:
      </p>
      <p>
        <strong>Thời gian gửi khiếu nại</strong>: Quý khách cần gửi khiếu nại
        trong vòng 24 giờ kể từ khi nhận hàng.
      </p>
      <p>
        <strong>Cách thức gửi thông tin</strong>: Quý khách có thể sử dụng chức
        năng khiếu nại trên website của chúng tôi:
      </p>
      <p>
        1. Đăng nhập vào tài khoản.<br></br>
        2. Chọn đơn hàng cần khiếu nại.<br></br>
        3. Lựa chọn trạng thái khiếu nại.<br></br>
        4. Cung cấp thông tin chi tiết (video kiểm kê sản phẩm, hình ảnh hàng
        hoá bị hư hỏng, mã vận đơn, hóa đơn nhà cung cấp, v.v...).
      </p>
      <p>
        <strong>Hình thức liên lạc</strong>: Chúng tôi sẽ liên hệ với Quý khách
        qua điện thoại hoặc thông qua phần khiếu nại trên đơn hàng.
      </p>
      <p>
        <strong>Thời gian phản hồi</strong>:
      </p>
      <p>
        Nếu khiếu nại liên quan đến bộ phận nghiệp vụ của công ty, chúng tôi sẽ
        phản hồi trong vòng 1 ngày.<br></br> Nếu khiếu nại liên quan đến nhà
        cung cấp, chúng tôi sẽ phản hồi trong vòng 3-4 ngày tuỳ thuộc vào sự hợp
        tác của nhà cung cấp.
      </p>
      <p id="complaint-boi-thuong" className="fw-bold policy-sub-title">
        2. Chính sách bồi thường:
      </p>
      <p>
        <strong>Mua hàng</strong>: Chúng tôi bồi thường 100% giá trị tiền hàng
        và phí mua hàng đối với các sản phẩm thiếu hoặc sai yêu cầu.
      </p>
      <p>
        <strong>Kiểm hàng</strong>: Chúng tôi bồi thường 100% giá trị tiền hàng
        và phí kiểm hàng đối với các sản phẩm thiếu hoặc không phát hiện lỗi sai
        theo quy định.
      </p>
      <p>
        <strong>Thất lạc hàng hoá</strong>: Nếu hàng hoá bị thất lạc do lỗi của
        ZTO VIỆT NAM, chúng tôi sẽ bồi thường 100% giá trị hàng hoá và các phí
        dịch vụ liên quan.
      </p>
      <p>
        <strong>Vận chuyển hàng hoá</strong>:
      </p>
      <p>
        Nếu hàng hoá bị thất lạc do bên vận chuyển thứ ba, ZTO VIỆT NAM không
        chịu trách nhiệm bồi thường. Quý khách cần tự thỏa thuận với đơn vị vận
        chuyển.<br></br> Nếu hàng hoá bị hư hỏng trong quá trình vận chuyển và
        Quý khách đã sử dụng dịch vụ đóng kiện gỗ, chúng tôi sẽ hoàn 100% giá
        trị thiệt hại và phí đóng gỗ nếu lỗi là do ZTO VIỆT NAM.<br></br> Nếu
        không sử dụng dịch vụ đóng gỗ, chúng tôi sẽ thương lượng mức bồi thường
        tùy theo mức độ thiệt hại.
      </p>
      <p>
        <strong>Hàng hóa quấn bóng khí</strong>: Nếu sản phẩm có nguy cơ vỡ hỏng
        cao và ZTO VIỆT NAM đã cảnh báo, chúng tôi không chịu trách nhiệm bồi
        thường nếu Quý khách không sử dụng dịch vụ quấn bóng khí. Nếu sử dụng
        dịch vụ, chúng tôi sẽ thương lượng mức bồi thường dựa trên thiệt hại
        thực tế.
      </p>
      <p>
        <strong>Đơn hàng ký gửi</strong>: ZTO VIỆT NAM không chịu trách nhiệm về
        tình trạng hư hỏng của hàng hoá trong các đơn hàng ký gửi. Nếu có bảo
        hiểm hàng hoá, chúng tôi sẽ bồi thường 50% giá trị hàng hoá nếu xảy ra
        mất mát hoặc hư hỏng.
      </p>
      <p id="complaint-luu-kho" className="fw-bold policy-sub-title">
        3. Quy định lưu kho và phí lưu kho:
      </p>
      <p>
        <strong>Thời gian lưu kho</strong>: Hàng hoá sẽ được lưu tại kho của ZTO
        VIỆT NAM không quá 5 ngày. Nếu cần gia hạn, Quý khách phải thông báo
        trước và có sự chấp thuận từ ZTO VIỆT NAM.
      </p>
      <p>
        <strong>Phí lưu kho</strong>: Sau 10 ngày kể từ khi hàng hoá về kho mà
        Quý khách chưa nhận, chúng tôi sẽ tính phí lưu kho 5% giá trị tổng đơn
        hàng/ngày.
      </p>
      <p id="complaint-ho-tro" className="fw-bold policy-sub-title">
        4. Chính sách hỗ trợ đối với các khiếu nại không nằm trong phạm vi cam
        kết dịch vụ
      </p>
      <p>
        ZTO VIỆT NAM sẽ hỗ trợ giải quyết các khiếu nại không thuộc phạm vi cam
        kết dịch vụ, bao gồm:
      </p>
      <p>
        • Khiếu nại đối với nhà cung cấp khi vi phạm điều khoản giao dịch.
        <br></br>• Khiếu nại đối với đơn hàng không sử dụng dịch vụ kiểm hàng.
      </p>
      <p>
        Chúng tôi sẽ yêu cầu sự hợp tác của Quý khách trong suốt quá trình xử lý
        khiếu nại để đảm bảo giải quyết nhanh chóng và chính xác.
      </p>
      <p id="complaint-luu-y" className="fw-bold policy-sub-title">
        5. Các lưu ý cần thiết
      </p>
      <p>
        • Chúng tôi khuyến khích Quý khách lựa chọn hàng hoá và phương thức đóng
        gói/vận chuyển phù hợp, cũng như chia sẻ rủi ro với ZTO VIỆT NAM khi xảy
        ra thiệt hại ngoài mong muốn.<br></br>• Việc đổi/trả hàng có nhiều rủi
        ro, chúng tôi không khuyến khích xử lý khiếu nại theo phương án này.
        <br></br>• Trong quá trình giải quyết khiếu nại, ZTO VIỆT NAM yêu cầu sự
        hợp tác chặt chẽ từ Quý khách để đảm bảo tính hiệu quả.
      </p>
    </div>
  );
};
